import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Card,
  Spin,
  Space,
  Button,
  Form,
  message,
  Typography,
  Upload,
} from "antd";
import {
  ArrowLeftOutlined,
  CloseOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Compressor from "compressorjs";

// Components
import FormJudge1 from "./FormJudge1";
import AddDefendantModal from "../../court/modalJudgment/AddDefendantModal";

// Hooks
import useJudgmentData from "../hooks/useJudgmentData";

const { Title } = Typography;

// หน้านี้รองรับเฉพาะกรณี "ทำยอม" (ประนีประนอมยอมความ) เท่านั้น
const radioDecide = "agreement";

const ReCheckJudgement = ({
  dataDefault,
  onClose,
  funcUpdateStatus,
  dataDB2,
}) => {
  const [form] = Form.useForm();
  const [formAddDefendant] = Form.useForm();

  // Custom Hook
  const {
    loading,
    setLoading,
    dataLoadGurantor,
    judgeData,
    lawsuitData,
    arothrData,
    dataNote,
    checkboxTab1,
    allDefendants,
    customerCustomerId,
    customerCustomerIdAll,
    nextGarno,
    userId,
    userName,
    loadGurantor,
    loadData,
    postStatus,
    addCustomerJudge,
    handleCheckboxTab1Change,
    resetCheckboxes,
    saveFormDataTab1,
    formDataTab1,
    resetFormDataTab1,
    selectedDefendantsTab1,
    handleUpLoadIMG,
  } = useJudgmentData({ dataDefault, form });

  // Local States

  const [isModalAddDefendant, setIsModalAddDefendant] = useState(false);
  const [arrow, setArrow] = useState("Show");

  // ✅ อัพโหลดเอกสารประกอบ: ไฟล์คำพิพากษา (imageType 19) และใบปิด (imageType 18)
  const IMAGE_TYPE_JUDGEMENT = 19;
  const IMAGE_TYPE_CLOSING = 18;
  const UPLOAD_MENU_TYPE = 10;

  const [fileListJudgement, setFileListJudgement] = useState([]);
  const [fileListClosing, setFileListClosing] = useState([]);
  const [allImg, setAllImg] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const compressImage = useCallback((file, types, index) => {
    return new Promise((resolve) => {
      let quality;
      if (file.size >= 2500000) quality = 0.1;
      else if (file.size >= 2000000) quality = 0.15;
      else if (file.size >= 1500000) quality = 0.2;
      else if (file.size >= 1000000) quality = 0.3;
      else quality = 0.5;

      new Compressor(file, {
        quality: quality,
        success: (result) => {
          const fileOptions = {
            type: result.type,
            lastModified: Date.now(),
          };
          const compressedFileAsFile = new File(
            [result],
            file.name,
            fileOptions,
          );

          const image = {
            imageCompressedFile: compressedFileAsFile,
            type: types,
            originalIndex: index,
          };

          setAllImg((prev) => [...prev, image]);
          resolve(true);
        },
        error: (err) => {
          console.error("Compressor error:", err);
          message.error(`ไม่สามารถบีบอัดไฟล์ "${file.name}" ได้`);
          resolve(true);
        },
      });
    });
  }, []);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = useCallback(async (file) => {
    if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
      let pdfUrl = file.url;
      if (!pdfUrl && file.originFileObj) {
        pdfUrl = URL.createObjectURL(file.originFileObj);
      }
      window.open(pdfUrl, "_blank");
      return;
    }

    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  }, []);

  const beforeUpts = useCallback(
    async (file, ty) => {
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      if (isPdf) {
        setAllImg((prev) => [
          ...prev,
          { imageCompressedFile: file, type: ty, originalIndex: file.uid },
        ]);
        return false;
      }

      if (file.size > 5000000) {
        message.warning(`ไฟล์ "${file.name}" เกิน 5MB`);
        return Upload.LIST_IGNORE;
      }

      try {
        await compressImage(file, ty, file.uid);
        return false;
      } catch (error) {
        console.error("File processing error:", error);
        message.error("File processing failed.");
        return Upload.LIST_IGNORE;
      }
    },
    [compressImage],
  );

  const handleChangeIMGJudgement = useCallback(
    ({ fileList: newFileList, file }) => {
      setFileListJudgement(newFileList);

      if (file.status === "removed") {
        setAllImg((prev) =>
          prev.filter((img) => img.originalIndex !== file.uid),
        );
      }
    },
    [],
  );

  const handleChangeIMGClosing = useCallback(
    ({ fileList: newFileList, file }) => {
      setFileListClosing(newFileList);

      if (file.status === "removed") {
        setAllImg((prev) =>
          prev.filter((img) => img.originalIndex !== file.uid),
        );
      }
    },
    [],
  );

  const iconRender = (file) => {
    if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
      return <FilePdfOutlined style={{ fontSize: "48px", color: "#ff4d4f" }} />;
    }
    return null;
  };

  const uploadPropsJudgement = useMemo(
    () => ({
      name: "file",
      multiple: true,
      accept: "image/*,.pdf",
      listType: "picture",
      fileList: fileListJudgement,
      onPreview: handlePreview,
      onChange: handleChangeIMGJudgement,
      beforeUpload: (file) => beforeUpts(file, IMAGE_TYPE_JUDGEMENT),
      iconRender,
    }),
    [fileListJudgement, handlePreview, handleChangeIMGJudgement, beforeUpts],
  );

  const uploadPropsReceived = useMemo(
    () => ({
      name: "file",
      multiple: true,
      accept: "image/*,.pdf",
      listType: "picture",
      fileList: fileListClosing,
      onPreview: handlePreview,
      onChange: handleChangeIMGClosing,
      beforeUpload: (file) => beforeUpts(file, IMAGE_TYPE_CLOSING),
      iconRender,
    }),
    [fileListClosing, handlePreview, handleChangeIMGClosing, beforeUpts],
  );

  const mergedArrow = useMemo(() => {
    if (arrow === "Hide") return false;
    if (arrow === "Show") return true;
    return { pointAtCenter: true };
  }, [arrow]);

  // ========================================
  // Auto-check ผู้เช่าซื้อใน Tab 1
  // ========================================

  useEffect(() => {
    loadGurantor();
    loadData();
  }, [loadGurantor, loadData]);

  useEffect(() => {
    resetFormDataTab1();
  }, [judgeData, arothrData, lawsuitData, resetFormDataTab1]);

  // หน้านี้รองรับเฉพาะ "ทำยอม" จึงต้องเลือกจำเลยทุกคนเป็นค่าเริ่มต้นเสมอ
  useEffect(() => {
    if (customerCustomerIdAll.length === 0) return;

    const isAllSelected =
      checkboxTab1.length === customerCustomerIdAll.length &&
      customerCustomerIdAll.every((id) => checkboxTab1.includes(id));

    if (!isAllSelected) {
      handleCheckboxTab1Change(customerCustomerIdAll);
    }
  }, [customerCustomerIdAll, checkboxTab1, handleCheckboxTab1Change]);

  const onChangeCheckBoxAll = useCallback(
    (e) => {
      const value = e.target.checked;
      resetCheckboxes();
      if (value) {
        handleCheckboxTab1Change(customerCustomerIdAll);
      } else {
        handleCheckboxTab1Change([customerCustomerId]);
      }
    },
    [
      customerCustomerIdAll,
      customerCustomerId,
      resetCheckboxes,
      handleCheckboxTab1Change,
    ],
  );

  // ========================================
  // Event Handlers
  // ========================================

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const onFinish = useCallback(
    async (values) => {
      try {
        console.log("Form Values:", values);
        console.log("Selected Defendants Tab1:", selectedDefendantsTab1);

        if (values.newContnoCustom === dataDefault.newContno) {
          message.error("เลขสัญญาซ้ำ กรุณาแก้เลขสัญญาใหม่");
          return;
        }

        let payload;
        let loanNewData;
        let payloadJudge;
        let judgeDefData = [];
        const newContnoUse =
          formDataTab1?.newContnoCustom || values?.newContnoCustom;

        // ── คำนวณยอดรวม extraCost_xxx (เทียบ _1 vs _2 เอาค่ามากกว่า) ──
        const calcExtraCostsTotal = (values) => {
          const extraKeys = Object.keys(values).filter((key) =>
            /^extraCost_\d+_[12]$/.test(key),
          );

          const codes = new Set(
            extraKeys.map((key) => key.match(/^extraCost_(\d+)_[12]$/)[1]),
          );

          let total = 0;
          codes.forEach((code) => {
            const val1 = parseFloat(values[`extraCost_${code}_1`] || 0);
            const val2 = parseFloat(values[`extraCost_${code}_2`] || 0);
            total += Math.max(val1, val2);
          });

          return total;
        };

        const extraCostsTotal = calcExtraCostsTotal(values); // เทียบ max แล้วรวม

        // ทำยอม: จำเลยที่เลือกไว้ใน governmentOfficer1
        let defendantNumberValue = 0;
        const checkBoxValue = allDefendants.filter((item) =>
          values.governmentOfficer1.includes(item.CUSTOMER_ID),
        );
        console.log("checkBoxValue", checkBoxValue);
        console.log("selectedDefendantsTab1", selectedDefendantsTab1);
        console.log("allDefendants", allDefendants);

        checkBoxValue.forEach((defendant) => {
          const isCustomer = defendant.type === "customer";
          const isCustomerId = defendant.CUSTOMER_ID;

          const allPayData = {
            CONTNO: dataDefault.CONTNO,
            customerId: isCustomerId,
            judgeNumber: 1,
            defendantNumber: defendantNumberValue + 1,
            newContno: newContnoUse,
            ref: isCustomer ? "001" : "002",

            TONBALANCE: parseFloat(values.paymentDue || 0),
            interestRate: values.interestRateJudgeLack1 * 100 || 0,

            interestLimit: 0,
            delay: null,
            firstDate: values.dateInterest1
              ? dayjs(values.dateInterest1).format()
              : null,

            trackingFee: values.trackingFeeEnforce1 || 0,
            attorneyFees: values.lawyerFeeEnforce1 || 0,
            fee: parseInt(values.courtFee1 || 0),
          };
          defendantNumberValue++;
          judgeDefData.push(allPayData);
        });

        const customerValue = allDefendants.find((item) => item.garno === 0);
        console.log("customerValue", customerValue);

        loanNewData = {
          TOT_UPAY: parseFloat(values.paymentPerMonthAmount || 0), // ค่างวด
          T_NOPAY: parseInt(values.costMonth3 || 0), // จำนวนงวด
          NCSHPRC: parseFloat(values.paymentDue || 0), // ยอด
          CUSTOMER_ID: customerValue.CUSTOMER_ID,
          NEW_CONTNO: newContnoUse,
          ISSUNO: dataDefault.CONTNO,
        };

        console.log("ทำยอม:", judgeDefData);
        console.log("allDefendants", allDefendants);

        const NULLABLE_FIELDS = [
          "judgeNumber",
          "TONBALANCE",
          "interestRate",
          "interestLimit",
          "delay",
          "firstDate",
          "costOfUselesenessPerMonth",
          "costOfUselessness",
          "costRate",
          "costInterestLimit",
          "costDelay",
          "costFirstDate",
          "costSdatePerMonth",
          "costOfUselesenessMonth",
          "trackingFee",
          "attorneyFees",
          "fee",
        ];

        console.log("judgeDefData", judgeDefData);
        console.log("judgeData", judgeData);

        if (judgeData?.judgeDef) {
          // ✅ existing customerIds ใน judgeDef เดิม (เพื่อหาตัวใหม่ที่เพิ่มเข้ามา)
          const existingCustomerIds = new Set(
            judgeData.judgeDef.map((d) => d.customerId),
          );

          const mergedJudgeDef = judgeData.judgeDef.map((originalDef) => {
            const updatedDef = judgeDefData.find(
              (d) => d.customerId === originalDef.customerId,
            );

            if (updatedDef) {
              // ✅ มีใน judgeDefData → ใช้ค่าใหม่
              console.log("updatedDef (found)", updatedDef);
              return { ...updatedDef };
            } else {
              // ❌ ไม่มีใน judgeDefData → ใช้ค่าเดิม แต่ null NULLABLE_FIELDS
              const nulled = { ...originalDef };
              NULLABLE_FIELDS.forEach((field) => {
                nulled[field] = null;
              });
              console.log("nulled (not found)", nulled);
              return nulled;
            }
          });

          // ✅ หาตัวที่ถูกเพิ่มใหม่ใน judgeDefData แต่ไม่มีใน judgeDef เดิม
          const newEntries = judgeDefData.filter(
            (d) => !existingCustomerIds.has(d.customerId),
          );
          console.log("newEntries (added)", newEntries);

          judgeDefData = [...mergedJudgeDef, ...newEntries];
          console.log("judgeDefData------>", judgeDefData);
        }

        let definedCostPermonthValue = 0;

        if (formDataTab1?.costPermonth1 ?? values?.costPermonth1) {
          if (formDataTab1?.costDay1 ?? values?.costDay1 ?? 1) {
            definedCostPermonthValue =
              (formDataTab1?.costPermonth1 ?? values?.costPermonth1) *
              (formDataTab1?.costDay1 ?? values?.costDay1 ?? 1);
          } else {
            definedCostPermonthValue =
              (formDataTab1?.costPermonth1 ?? values?.costPermonth1) * 1;
          }
        }

        // สร้าง payload หลัก
        payloadJudge = {
          ...judgeData,
          idJudge: judgeData?.idJudge || 0,
          loanId: dataDefault.loanId,
          lawsuitId: dataDefault.lawsuitId,
          userId: userId,
          CONTNO: dataDefault.CONTNO,
          mark: values.memo || null,
          redCaseNumber: values.redNumber,
          judgeDate: dayjs(values.enforceCaseDate).format(),

          judgement: parseFloat(values.judgement1 || 0),
          judgeInterestRate: values.interestRate1 * 100 || 0,

          judgementLack: parseFloat(values.judgementLack1 || 0),
          interestRate: values.interestRateJudgeLack1 * 100 || 0,

          lackOfBenefits: parseFloat(values.costUnless1 || 0) || 0,
          interestRateOfLack: values.interestRateCostUnless1 * 100 || 0,
          LACK_BALANCE: parseFloat(values.costUnless1 || 0) || 0,

          fee: parseFloat(values.courtFee1 || 0) || 0,
          trackingFee: parseFloat(values.trackingFeeEnforce1 || 0),
          attorneyFees: parseFloat(values.lawyerFeeEnforce1 || 0),

          copyingFee: 0,
          trialMoneyCleared: 0,
          suspensionAmount: 0,

          AROTHR: 0,

          TONBALANCE: parseFloat(values.paymentDue),
          judgementInterest:
            parseFloat(values.judgementLack1 || 0) > 0 &&
            parseFloat(values.judgement1 || 0) >
              parseFloat(values.judgementLack1 || 0)
              ? Math.round(
                  parseFloat(values.judgement1 || 0) -
                    parseFloat(values.judgementLack1 || 0),
                )
              : 0,
          judgeDef: judgeDefData,
          loanNew: loanNewData ? loanNewData : null,
          FDATE: dayjs(values.dueDate).format(),
          newContno: newContnoUse,
          mainOther: extraCostsTotal,
        };

        payload = {
          arothr: [],
          judge: payloadJudge,
          userId: userId,
          loanId: dataDefault.loanId,
          blackCaseNumber: values.blackNumber,
          mark: values.memo,
          FDATE: dayjs(values.dueDate).format(),
          TOT_UPAY: parseFloat(values.paymentPerMonthAmount || 0), // ค่างวด
          T_NOPAY: parseInt(values.costMonth3 || 0), // จำนวนงวด
          annualInterestRate: values.interestRate1 * 100 || 0,
        };

        console.log("📦 Payload:", payload);
        console.log("allImg", allImg);
        console.log("UPLOAD_MENU_TYPE", UPLOAD_MENU_TYPE);

        setLoading(true);
        const result = await postStatus(payload);

        if (result !== 200) {
          throw new Error("ไม่สามารถบันทึกข้อมูลได้");
        }

        console.log("✅ ขั้นตอนที่ 1: บันทึกข้อมูลคำพิพากษาสำเร็จ");
        if (allImg.length > 0) {
          try {
            console.log(
              `📤 ขั้นตอนที่ 2: กำลังอัปโหลดรูปภาพ ${allImg.length} ไฟล์...`,
            );

            const uploadResult = await handleUpLoadIMG(
              allImg,
              UPLOAD_MENU_TYPE,
              {
                id: dataDefault.loanId,
              },
            );

            if (uploadResult === 200) {
              console.log("✅ ขั้นตอนที่ 2: อัปโหลดรูปภาพสำเร็จ");
            }
          } catch (uploadError) {
            console.error("❌ อัปโหลดรูปภาพล้มเหลว:", uploadError);
            message.warning({
              content: "บันทึกข้อมูลสำเร็จ แต่อัปโหลดรูปภาพล้มเหลว",
              duration: 4,
            });
          }
        }

        if (funcUpdateStatus) {
          try {
            console.log("🔄 ขั้นตอนที่ 3: กำลังอัปเดทสถานะ...");
            await funcUpdateStatus();
            console.log("✅ ขั้นตอนที่ 3: อัปเดทสถานะสำเร็จ");
          } catch (updateError) {
            console.error("❌ อัปเดทสถานะล้มเหลว:", updateError);
            // ไม่ throw error เพราะข้อมูลหลักบันทึกสำเร็จแล้ว
          }
        }

        message.success({
          content: "บันทึกคำพิพากษาสำเร็จ",
          duration: 2,
        });

        // Delay เล็กน้อยเพื่อให้ user เห็น success message
        setTimeout(() => {
          handleCancel();
        }, 500);
      } catch (error) {
        console.error("❌ เกิดข้อผิดพลาด:", error);

        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง";

        message.error({
          content: errorMessage,
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      dataDefault,
      userId,
      selectedDefendantsTab1,
      allDefendants,
      postStatus,
      funcUpdateStatus,
      handleCancel,
      arothrData,
      formDataTab1,
      judgeData,
      userName,
      setLoading,
      allImg,
      handleUpLoadIMG,
    ],
  );

  const onFinishFailed = useCallback((errorInfo) => {
    console.log("Failed:", errorInfo);
    message.error("กรุณากรอกข้อมูลที่มีเครื่องหมาย * ให้ครบถ้วน");
  }, []);

  const onChangeDate = (date, dateString) => {
    console.log(date, dateString);
  };

  const onChangeDateAgreement = (date, dateString) => {
    console.log(date, dateString);
  };

  // ========================================
  // Modal Handlers
  // ========================================

  const handleOpenAddDefendant = useCallback(() => {
    setIsModalAddDefendant(true);
    formAddDefendant.resetFields();
  }, [formAddDefendant]);

  const handleAddDefendant = async () => {
    try {
      const values = await formAddDefendant.validateFields();
      await addCustomerJudge(values);
      setIsModalAddDefendant(false);
      formAddDefendant.resetFields();
    } catch (error) {
      console.log("Add defendant failed:", error);
    }
  };

  // ========================================
  // Render
  // ========================================

  return (
    <div
      style={{
        padding: "12px",
        maxWidth: 1400,
        margin: "0 auto",
        minHeight: "100vh",
        background: "#f0f2f5",
      }}
    >
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        <Card
          title={
            <Space align="center" size="large">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleCancel}
                type="text"
                size="large"
                style={{ fontSize: 16 }}
              >
                ย้อนกลับ
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                {`เปลี่ยนคำพิพากษา: ${dataDefault?.CONTNO || ""}`}
              </Title>
            </Space>
          }
          extra={
            <Button
              danger
              onClick={handleCancel}
              size="large"
              icon={<CloseOutlined />}
            >
              ปิด
            </Button>
          }
          variant={false}
          style={{
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderRadius: 12,
          }}
        >
          {/* Content: รองรับเฉพาะกรณี "ทำยอม" */}
          <Title level={4} style={{ textAlign: "center", marginBottom: 24 }}>
            🤝 คำพิพากษาจำเลย (ทำยอม)
          </Title>
          <FormJudge1
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onChangeDate={onChangeDate}
            onChangeDateAgreement={onChangeDateAgreement}
            radioDecide={radioDecide}
            dataDefault={dataDefault}
            checkboxTab1={checkboxTab1}
            allDefendants={allDefendants}
            customerCustomerId={customerCustomerId}
            handleCheckboxTab1Change={handleCheckboxTab1Change}
            onAddDefendantClick={handleOpenAddDefendant}
            handleCancel={handleCancel}
            loading={loading}
            mergedArrow={mergedArrow}
            saveFormDataTab1={saveFormDataTab1}
            onChangeCheckBoxAll={onChangeCheckBoxAll}
            dataNote={dataNote}
            arothrData={arothrData}
            uploadPropsJudgement={uploadPropsJudgement}
            uploadPropsReceived={uploadPropsReceived}
          />
        </Card>
      </Spin>

      {/* Modal เพิ่มจำเลย */}
      <AddDefendantModal
        open={isModalAddDefendant}
        onCancel={() => {
          setIsModalAddDefendant(false);
          formAddDefendant.resetFields();
        }}
        onOk={handleAddDefendant}
        form={formAddDefendant}
        loading={loading}
        nextGarno={nextGarno}
        guarantorsLength={dataLoadGurantor?.guarantors?.length}
      />
    </div>
  );
};

export default ReCheckJudgement;
